import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { get, post } from '../api';
import { Problems } from '../components/Field';
import { FilterChips } from '../components/FilterChips';
import { SectionTabs } from '../components/SectionTabs';
import { currentLocale } from '../dates';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { formatMoney } from '../money';
import { BillsTab } from './Bills';
import { InvoicesTab } from './Invoices';
import { useMoneyFilter } from './moneyFilter';
import { MoneyOverview, type MoneySummary } from './MoneyOverview';

export interface Status {
  label: string;
  variant: string;
  url: string;
}
interface Row {
  id: number;
  customer_name: string | null;
  client_id: number | null;
  client_name: string | null;
  lead_name: string | null;
  held: boolean;
  start: string;
  status_reason: string;
  charge_amount: string;
  pay_amount: string;
  can_invoice: boolean;
  /** Its time has passed but nobody marked it complete, so it cannot be invoiced yet. */
  needs_completing: boolean;
  client: Status;
  contractor: Status;
}
interface Ledger {
  total: number;
  counts: Record<string, number>;
  labels: Record<string, string>;
  rows: Row[];
}

const when = (iso: string) =>
  new Date(iso).toLocaleDateString(currentLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

/** A status from the server as a pill: its variant names the pill's tone. */
// "Not invoiced" and "Not billed" are the normal state of a new visit, so they stay quiet.
const PILL: Record<string, string> = {
  info: 'pill info',
  warn: 'pill warn',
  completed: 'pill ok',
  danger: 'pill bad',
};
export function StatusPill(props: { status: Status }) {
  const { label, variant, url } = props.status;
  const pill = <span className={PILL[variant] ?? 'pill'}>{label}</span>;
  return url ? <Link to={url}>{pill}</Link> : pill;
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'visits', label: 'Visits' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'bills', label: 'Bills' },
];

/**
 * Where the business's money stands: the week's jobs on the Overview, and the
 * visits, client invoices and contractor bills they are done from.
 */
export function Money() {
  const [params] = useSearchParams();
  const location = useLocation();
  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab')! : 'overview';
  const summary = useLoad(() => get<MoneySummary>('/api/money/summary'), [location.key]);
  const s = summary.data;
  const counts: Record<string, number | undefined> = s
    ? {
        visits: s.counts.to_invoice,
        invoices: s.invoices.draft.count + s.invoices.sent.count + s.invoices.authorised.count,
        bills: s.bills.received.count + s.bills.matched.count + s.bills.disputed.count,
      }
    : {};
  return (
    <>
      <h1>Money</h1>
      <SectionTabs
        label="Money sections"
        tabs={TABS.map((t) => ({
          key: t.key,
          label: counts[t.key] ? `${t.label} (${counts[t.key]})` : t.label,
        }))}
        current={tab}
        to={(k) => (k === 'overview' ? '/money' : `/money?tab=${k}`)}
      />
      {tab === 'overview' &&
        (s ? (
          <MoneyOverview summary={s} />
        ) : summary.error ? (
          <p role="alert" className="error">
            {summary.error}
          </p>
        ) : (
          <p className="muted">Loading…</p>
        ))}
      {tab === 'visits' && <VisitsTab />}
      {tab === 'invoices' && <InvoicesTab />}
      {tab === 'bills' && <BillsTab />}
    </>
  );
}

/** What each Visits filter holds, said when it holds nothing. */
const EMPTY: Record<string, string> = {
  to_invoice:
    'Nothing to invoice. Finished visits wait here until they go on an invoice to the client, and past visits nobody marked complete show here too.',
  invoicing: 'No invoices waiting to be paid. Visits on an unpaid invoice show here.',
  to_bill: 'Every finished visit has been billed by its contractor.',
  bill_unpaid: 'No contractor bills waiting to be paid.',
  settled: 'Nothing settled yet. A visit lands here once the client and the contractor are paid.',
};

const PER_PAGE = 100;
const cents = (x: string) => Math.round(Number(x || 0) * 100);

/**
 * Every done visit and where its money stands, for looking things up. The
 * invoicing itself is done in the invoice run, client by client.
 */
function VisitsTab() {
  const [filter, setFilter] = useMoneyFilter('visits', 'to_invoice');
  const [page, setPage] = useState(1);
  const [tick, setTick] = useState(0);
  const [problems, setProblems] = useState<string[]>([]);
  const [message, setMessage] = useState('');

  const ledger = useLoad(
    () => get<Ledger>(`/api/money/ledger?filter=${filter}&per_page=${PER_PAGE}&page=${page}`),
    [filter, page, tick],
  );
  const rows = ledger.data?.rows ?? [];
  const total = ledger.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));
  const invoicing = filter === 'to_invoice';

  // On "To invoice", visits are grouped by who is billed, as an invoice goes to one client.
  const groups = invoicing
    ? [...new Set(rows.map((r) => r.client_id))].map((id) => {
        const of = rows.filter((r) => r.client_id === id);
        const ready = of.filter((r) => r.can_invoice);
        return {
          id,
          name: of[0]?.client_name ?? 'No client',
          rows: of,
          ready,
          unfinished: of.filter((r) => r.needs_completing).length,
          amount: ready.reduce((s, r) => s + cents(r.charge_amount), 0) / 100,
        };
      })
    : [{ id: null, name: '', rows, ready: [], unfinished: 0, amount: 0 }];

  async function release(r: Row) {
    setProblems([]);
    setMessage('');
    try {
      await post(`/api/visits/${r.id}/release`);
      setMessage(`${r.customer_name ?? 'The visit'}, ${when(r.start)}, can be invoiced again.`);
      setTick((t) => t + 1);
    } catch (e) {
      setProblems(describe(e).problems);
    }
  }

  // Completing a past visit here puts it straight on the list to invoice.
  async function complete(r: Row) {
    setProblems([]);
    setMessage('');
    try {
      await post(`/api/visits/${r.id}/complete`);
      setMessage(
        `${r.customer_name ?? 'The visit'}, ${when(r.start)}, is complete and ready to invoice.`,
      );
      setTick((t) => t + 1);
    } catch (e) {
      setProblems(describe(e).problems);
    }
  }

  const pick = (k: string) => {
    setPage(1);
    setMessage('');
    setFilter(k);
  };

  return (
    <div className="money-tab">
      <div className="list-bar">
        {ledger.data && (
          <FilterChips
            label="Show"
            current={filter}
            chips={Object.entries(ledger.data.labels).map(([key, label]) => ({
              key,
              label,
              count: ledger.data?.counts[key] ?? 0,
            }))}
            onPick={pick}
          />
        )}
        {invoicing && rows.some((r) => r.can_invoice) && (
          <div className="bar-action">
            <Link className="button" to="/money/invoice-run">
              Start the invoice run
            </Link>
          </div>
        )}
      </div>
      <Problems problems={problems} title="Could not do that." />
      {message && (
        <p className="notice" role="status">
          {message}
        </p>
      )}
      {ledger.error && (
        <p role="alert" className="error">
          {ledger.error}
        </p>
      )}
      {ledger.loading && !ledger.data && <p className="muted">Loading…</p>}
      {!ledger.loading && !ledger.error && rows.length === 0 && (
        <p className="empty">{EMPTY[filter] ?? 'No visits here.'}</p>
      )}
      {rows.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Customer</th>
                <th>Lead</th>
                <th className="num">Charge</th>
                <th className="num">Pay</th>
                <th>Invoice</th>
                <th>Bill</th>
              </tr>
            </thead>
            {groups.map((g) => {
              return (
                <tbody key={g.id ?? 'all'}>
                  {invoicing && (
                    <tr className="client-group">
                      <th scope="rowgroup" colSpan={3}>
                        {g.name}
                      </th>
                      <td className="num">{g.ready.length > 0 ? formatMoney(g.amount) : ''}</td>
                      <td colSpan={3} className="muted">
                        {g.ready.length} ready
                        {g.unfinished > 0 && ` · ${g.unfinished} to mark complete`}
                      </td>
                    </tr>
                  )}
                  {g.rows.map((r) => (
                    <tr key={r.id} className={r.needs_completing ? 'unfinished' : undefined}>
                      <td className={r.needs_completing ? 'status' : undefined}>
                        {r.needs_completing ? (
                          <>
                            <Link to={`/visits/${r.id}`}>{when(r.start)}</Link>
                            <br />
                            <button
                              type="button"
                              className="quiet"
                              onClick={() => void complete(r)}
                            >
                              Mark complete
                              <span className="sr-only">
                                {' '}
                                {r.customer_name}, {when(r.start)}
                              </span>
                            </button>
                          </>
                        ) : (
                          when(r.start)
                        )}
                      </td>
                      <td>{r.customer_name}</td>
                      <td>{r.lead_name ?? '—'}</td>
                      <td className="num">{formatMoney(r.charge_amount)}</td>
                      <td className="num">{formatMoney(r.pay_amount)}</td>
                      <td className="status">
                        <StatusPill status={r.client} />
                        {r.held && (
                          <button type="button" className="quiet" onClick={() => void release(r)}>
                            Release
                            <span className="sr-only">
                              {' '}
                              {r.customer_name}, {when(r.start)}
                            </span>
                          </button>
                        )}
                      </td>
                      <td className="status">
                        <StatusPill status={r.contractor} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              );
            })}
          </table>
        </div>
      )}
      {pages > 1 && (
        <nav className="pager" aria-label="Pages">
          <button
            type="button"
            className="quiet"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Newer
          </button>
          <span className="muted">
            Page {page} of {pages} · {total} visits
          </span>
          <button
            type="button"
            className="quiet"
            disabled={page >= pages}
            onClick={() => setPage(page + 1)}
          >
            Older
          </button>
        </nav>
      )}
    </div>
  );
}
